import { computed, linkedSignal, Signal } from '@angular/core';
import { notifier } from '../notifier/notifier.signal';
import {
  createPredicateFunctionFromStringFunction,
  UniqueIdentifierPredicate,
} from './create-predicate-function-from-string.function';
import { defaultPredicate } from './default-predicate.function';

export type SelectionSignal<T extends object | string | number | Date> = Signal<T[]> & {
  select: (...values: T[]) => void;
  deselect: (...values: T[]) => void;
  toggle: (...values: T[]) => void;
  setSelection: (...values: T[]) => void;
  clear: () => void;
  isSelected: (value: T) => Signal<boolean>;
  size: Signal<number>;
  isEmpty: Signal<boolean>;
  hasValue: Signal<boolean>;
};

export type SelectionSignalManualNotify<T extends object | string | number | Date> = Signal<T[]> &
  SelectionSignal<T> & {
    clearUnnotifiedSelection: () => void;
    notify: () => void;
  };

export interface TtSelectionOptions<T extends object | string | number | Date> {
  multiple?: boolean;

  predicate?: string | UniqueIdentifierPredicate<T>;

  initiallySelectedValues?: T[];
}

export function ttSelection<T extends object | string | number | Date>(
  options: () => TtSelectionOptions<T> = () => ({}),
): SelectionSignal<T> {
  const selectionSignalImpl = new SelectionSignalImpl<T>(false, options);
  const selectionSignal = selectionSignalImpl.selected as SelectionSignal<T>;

  selectionSignal.select = selectionSignalImpl.select.bind(selectionSignalImpl);
  selectionSignal.deselect = selectionSignalImpl.deselect.bind(selectionSignalImpl);
  selectionSignal.toggle = selectionSignalImpl.toggle.bind(selectionSignalImpl);
  selectionSignal.setSelection = selectionSignalImpl.setSelection.bind(selectionSignalImpl);
  selectionSignal.clear = selectionSignalImpl.clear.bind(selectionSignalImpl);
  selectionSignal.isSelected = selectionSignalImpl.isSelected.bind(selectionSignalImpl);
  selectionSignal.size = selectionSignalImpl.size().bind(selectionSignalImpl);
  selectionSignal.isEmpty = selectionSignalImpl.isEmpty().bind(selectionSignalImpl);
  selectionSignal.hasValue = selectionSignalImpl.hasValue().bind(selectionSignalImpl);

  return selectionSignal;
}

export function ttSelectionWithManualNotify<T extends object | string | number | Date>(
  options: () => TtSelectionOptions<T> = () => ({}),
): SelectionSignalManualNotify<T> {
  const selectionSignalImpl = new SelectionSignalImpl<T>(true, options);
  const selectionSignal = selectionSignalImpl.selected as SelectionSignalManualNotify<T>;

  selectionSignal.select = selectionSignalImpl.select.bind(selectionSignalImpl);
  selectionSignal.deselect = selectionSignalImpl.deselect.bind(selectionSignalImpl);
  selectionSignal.toggle = selectionSignalImpl.toggle.bind(selectionSignalImpl);
  selectionSignal.setSelection = selectionSignalImpl.setSelection.bind(selectionSignalImpl);
  selectionSignal.clear = selectionSignalImpl.clear.bind(selectionSignalImpl);
  selectionSignal.isSelected = selectionSignalImpl.isSelected.bind(selectionSignalImpl);
  selectionSignal.size = selectionSignalImpl.size().bind(selectionSignalImpl);
  selectionSignal.isEmpty = selectionSignalImpl.isEmpty().bind(selectionSignalImpl);
  selectionSignal.hasValue = selectionSignalImpl.hasValue().bind(selectionSignalImpl);
  selectionSignal.clearUnnotifiedSelection =
    selectionSignalImpl.clearUnnotifiedSelection.bind(selectionSignalImpl);
  selectionSignal.notify = selectionSignalImpl.notifier.notify;

  return selectionSignal;
}

class SelectionSignalImpl<T extends object | string | number | Date> {
  private internalSelectionMap: Map<string | number, T>;

  private previouslyNotifiedSelection: Map<string | number, T>;

  public readonly notifier = notifier();

  /**
   * Unique identifier to identify the element.
   */
  private readonly predicate: Signal<UniqueIdentifierPredicate<T>>;

  /**
   * if true multiple elements may be selected otherwise only one may be selected
   */
  private readonly multiple: Signal<boolean>;

  private readonly manualNotifier: boolean;

  public readonly selected = computed(() => {
    this.notifier();
    return [...this.internalSelectionMap.values()] as T[];
  });

  constructor(manualNotifier: boolean, options: () => TtSelectionOptions<T>) {
    this.manualNotifier = manualNotifier;
    this.predicate = linkedSignal({
      source: options,
      computation: ({ predicate }) => {
        if (!predicate) {
          return defaultPredicate;
        }
        return typeof predicate === 'string'
          ? createPredicateFunctionFromStringFunction(predicate)
          : predicate;
      },
    });
    this.multiple = linkedSignal({
      source: options,
      computation: ({ multiple }) => multiple ?? false,
    });
    this.internalSelectionMap = new Map<string | number, T>(
      (options().initiallySelectedValues ?? []).map((item) => [this.predicate()(item), item]),
    );
    this.previouslyNotifiedSelection = this.internalSelectionMap;
  }

  public select(...elements: T[]): void {
    if (Array.isArray(elements)) {
      this.assertValueLimit(elements);
      for (const element of elements) {
        this.markSelected(element);
      }
    } else {
      this.markSelected(elements);
    }

    if (!this.manualNotifier) {
      this.notifier.notify();
      this.previouslyNotifiedSelection = this.internalSelectionMap;
    }
  }

  public deselect(...elements: T[]): void {
    if (Array.isArray(elements)) {
      this.assertValueLimit(elements);
      for (const element of elements) {
        this.unmarkSelected(element);
      }
    } else {
      this.unmarkSelected(elements);
    }

    if (!this.manualNotifier) {
      this.notifier.notify();
      this.previouslyNotifiedSelection = this.internalSelectionMap;
    }
  }

  public toggle(...elements: T[]): void {
    if (Array.isArray(elements)) {
      for (const element of elements) {
        if (this.hasSelection(element)) {
          this.unmarkSelected(element);
          continue;
        }
        this.markSelected(element);
      }
    } else {
      if (this.hasSelection(elements)) {
        this.unmarkSelected(elements);
      } else {
        this.markSelected(elements);
      }
    }

    if (!this.manualNotifier) {
      this.notifier.notify();
      this.previouslyNotifiedSelection = this.internalSelectionMap;
    }
  }

  public setSelection(...elements: T[]): void {
    this.clearInternalSelection();
    this.select(...elements);
  }

  public clear() {
    this.clearInternalSelection();
    if (!this.manualNotifier) {
      this.notifier.notify();
      this.previouslyNotifiedSelection = this.internalSelectionMap;
    }
  }

  public size(): Signal<number> {
    return computed(() => {
      this.notifier();
      return this.internalSelectionMap.size;
    });
  }

  public isSelected(element: T): Signal<boolean> {
    return computed(() => {
      this.notifier();
      return this.internalSelectionMap.has(this.predicate()(element));
    });
  }

  public isEmpty(): Signal<boolean> {
    return computed(() => {
      this.notifier();
      return this.internalSelectionMap.size === 0;
    });
  }

  public hasValue(): Signal<boolean> {
    return computed(() => {
      this.notifier();
      return this.internalSelectionMap.size > 0;
    });
  }

  public clearUnnotifiedSelection() {
    this.internalSelectionMap = this.previouslyNotifiedSelection;
  }

  private clearInternalSelection() {
    this.internalSelectionMap.forEach((value) => this.unmarkSelected(value));
  }

  private markSelected(element: T): boolean {
    if (!this.hasSelection(element)) {
      if (!this.multiple()) {
        this.clearInternalSelection();
      }
      this.internalSelectionMap.set(this.predicate()(element), element);
      return true;
    }
    return false;
  }

  private unmarkSelected(element: T): void {
    if (this.hasSelection(element)) {
      this.internalSelectionMap.delete(this.predicate()(element));
    }
  }

  /**
   * @inheritDoc
   */
  private hasSelection(element: T): boolean {
    if (this.internalSelectionMap.size === 0) {
      return false;
    }

    return this.internalSelectionMap.has(this.predicate()(element));
  }

  /**
   * Verifies the value assignment and throws an error if the specified value array is
   * including multiple values while the selection is not supporting multiple values.
   */
  private assertValueLimit(values: unknown[]): void {
    if (values.length > 1 && !this.multiple()) {
      throw Error('Cannot pass multiple values into ttSelection with single-value mode.');
    }
  }
}
